import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const DealFormStepper = ({
  steps,
  currentStep,
  stepValidation,
  onGoToStep,
}) => {
  const current = steps.find((s) => s.id === currentStep);
  const progress = Math.round((currentStep / steps.length) * 100);

  const stepCircleClass = (step) => {
    if (currentStep === step.id) {
      return "bg-[#FF6C6C] text-white shadow-lg shadow-[#FF6C6C]/30";
    }
    if (stepValidation[step.id] || step.id < currentStep) {
      return "bg-green-500 text-white";
    }
    return "bg-gray-200 text-gray-500";
  };

  const connectorClass = (step) =>
    step.id < currentStep ? "bg-green-500" : "bg-gray-200";

  return (
    <div className="sticky top-16 z-30 border-b border-gray-200/80 bg-gray-50/95 shadow-sm backdrop-blur-md">
      <div className="px-4 py-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          {/* Desktop */}
          <div className="hidden justify-between items-center w-full xl:flex">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onGoToStep(step.id)}
                  className={`flex items-center gap-2 transition-all duration-300 flex-shrink-0 ${
                    step.id <= currentStep ? "opacity-100" : "opacity-50"
                  }`}
                >
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 flex-shrink-0 ${stepCircleClass(step)}`}
                  >
                    {stepValidation[step.id] && step.id < currentStep ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <div className="min-w-0 max-w-[120px] text-left">
                    <h3
                      className={`font-semibold text-xs transition-colors truncate ${
                        currentStep === step.id
                          ? "text-[#FF6C6C]"
                          : "text-gray-700"
                      }`}
                    >
                      {step.title}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      {step.subtitle}
                    </p>
                  </div>
                </motion.button>
                {index < steps.length - 1 && (
                  <div className="flex-1 mx-2">
                    <div
                      className={`h-1 rounded-full transition-colors duration-300 ${connectorClass(step)}`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Tablet */}
          <div className="hidden justify-between items-center px-4 w-full lg:flex xl:hidden">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onGoToStep(step.id)}
                  className={`flex flex-col items-center transition-all duration-300 flex-shrink-0 ${
                    step.id <= currentStep ? "opacity-100" : "opacity-50"
                  }`}
                >
                  <div
                    className={`flex items-center justify-center w-12 h-12 rounded-full transition-all duration-300 mb-2 ${stepCircleClass(step)}`}
                  >
                    {stepValidation[step.id] && step.id < currentStep ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <step.icon className="w-6 h-6" />
                    )}
                  </div>
                  <div className="text-center min-w-[100px] max-w-[120px]">
                    <h3
                      className={`font-semibold text-sm transition-colors whitespace-nowrap ${
                        currentStep === step.id
                          ? "text-[#FF6C6C]"
                          : "text-gray-700"
                      }`}
                    >
                      {step.title}
                    </h3>
                    <p className="text-xs text-gray-500 whitespace-nowrap mt-0.5">
                      {step.subtitle}
                    </p>
                  </div>
                </motion.button>
                {index < steps.length - 1 && (
                  <div className="flex-1 mx-2">
                    <div
                      className={`h-1 rounded-full transition-colors duration-300 ${connectorClass(step)}`}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onGoToStep(step.id)}
                    className={`flex flex-col items-center transition-all duration-300 flex-shrink-0 ${
                      step.id <= currentStep ? "opacity-100" : "opacity-50"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 mb-1 ${stepCircleClass(step)}`}
                    >
                      {stepValidation[step.id] && step.id < currentStep ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <step.icon className="w-4 h-4" />
                      )}
                    </div>
                    <div className="text-center max-w-[60px]">
                      <h3
                        className={`font-semibold text-xs transition-colors truncate ${
                          currentStep === step.id
                            ? "text-[#FF6C6C]"
                            : "text-gray-700"
                        }`}
                      >
                        {step.title}
                      </h3>
                    </div>
                  </motion.button>
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-1">
                      <div
                        className={`h-1 rounded-full transition-colors duration-300 ${connectorClass(step)}`}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="pt-4 mt-4 border-t border-gray-100">
            <div className="flex gap-3 justify-between items-center mb-2">
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">
                  Step {currentStep} of {steps.length}
                </span>
                {current?.subtitle ? (
                  <span className="hidden text-gray-500 sm:inline">
                    {" "}
                    · {current.subtitle}
                  </span>
                ) : null}
              </p>
              <span className="text-sm font-semibold text-[#FF6C6C]">
                {progress}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F]"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealFormStepper;
